import { useCallback, useEffect, useRef } from "react";
import { Flex, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";
import {
  Release,
  ReleasePricingType,
  ReleaseUserPurchase,
} from "../../../types";
import {
  FunnelComponentProps,
  FunnelContextTypes,
  FunnelOnSuccessResult,
} from "../../funnel/types";
import {
  createReleasePurchaseFromConfirmationToken,
  createReleasePurchaseFromSavedPaymentMethod,
  isReleasePaymentSetupPayload,
  ReleaseCheckoutSessionPayload,
  ReleasePaymentSetupPayload,
} from "../api/paymentApi";
import { PurchaseButton } from "./PurchaseButton";
import { useReleasePricingInfo } from "../../../hooks/useReleasePricingInfo";
import { ClaimWithGoldButton } from "../../cards/ClaimWithGoldButton";
import { useUserData } from "../../../hooks/useUserData";
import { useIsShowingEmbeddedStripeForm } from "../hooks/useIsShowingEmbeddedStripeForm";
import { EmbeddedStripePaymentForm } from "./EmbeddedStripePaymentForm";
import { FUNNEL_TYPES } from "../../funnel/utils/contants";
import { patchAllQueriesWithCompletedEntity } from "../utils/replaceCompletedEntityInCache";
import { getRuntimeReleaseUuid, isMatchingReleaseEntity } from "../utils/cacheMatchers";
import { createSessionPayloadStore } from "../utils/sessionPayloadStore";
import {
  saveGuestPendingPurchase,
  clearGuestPendingPurchase,
} from "../utils/guestPendingPurchase";
import { usePaymentCompletion } from "../hooks/usePaymentCompletion";
import { usePendingAuthPurchaseResume } from "../hooks/usePendingAuthPurchaseResume";
import {
  GET_COMIC_RELEASES_QUERY_KEY,
  GET_RELEASE_QUERY_KEY,
} from "../../../screens/reader/api/readerScreenApi";
import {
  clearPendingGoldReleaseAutoPurchase,
  hasPendingGoldReleaseAutoPurchase,
} from "../utils/pendingGoldReleaseAutoPurchase";

const releasePayloadStore = createSessionPayloadStore<ReleasePaymentSetupPayload>(
  "pendingReleasePaymentSetup",
  isReleasePaymentSetupPayload,
);

function buildHostedCheckoutReleaseUserPurchase(
  releaseId: number,
): ReleaseUserPurchase {
  const now = new Date();

  return {
    entity_type: "ReleaseUserPurchase",
    id: -1,
    token: "",
    release_id: releaseId,
    user_id: 0,
    session_id: 0,
    net_price: "0.00",
    is_downloadable: 0,
    is_active: 1,
    context_entity_type_id: null,
    context_entity_id: null,
    creator_id: 0,
    updater_id: 0,
    update_time: now,
    create_time: now,
    download_url: "",
    can_moderate: false,
  };
}

function buildHostedCheckoutReleaseSuccessResult(
  release: Release,
): FunnelOnSuccessResult {
  const purchaseRelease: Release = release.release_user_purchase
    ? release
    : {
        ...release,
        release_user_purchase: buildHostedCheckoutReleaseUserPurchase(
          release.id,
        ),
      };

  return {
    context: {
      purchaseRelease,
    } as FunnelContextTypes,
  };
}

export function PurchaseReleaseModalFooter({
  release,
  onClose,
  onSuccess: onSuccessProp,
  setFunnelType,
  autoPurchaseReleaseWithSavedPaymentMethod,
}: FunnelComponentProps) {
  const pricingInfo = useReleasePricingInfo(release as Release);
  const { userData } = useUserData();
  const queryClient = useQueryClient();
  const showEmbeddedForm = useIsShowingEmbeddedStripeForm();

  const { type, pricePointToUse } = pricingInfo;

  const pricePoint = pricePointToUse ?? release?.ppb_price_point ?? 1;

  const price = pricePoint / 100;

  const isAGoldPricePoint =
    type === ReleasePricingType.GoldDiscount ||
    type === ReleasePricingType.OnGoldSale;

  const buildReleaseCheckoutPayload =
    useCallback((): ReleaseCheckoutSessionPayload => {
      return {
        price_point_id: pricePoint,
        is_gold: isAGoldPricePoint,
        full_price: release?.ppb_price_point
          ? parseFloat((release.ppb_price_point / 100).toFixed(2))
          : undefined,
        is_sale: pricingInfo.isOnSale,
      };
    }, [
      isAGoldPricePoint,
      pricePoint,
      pricingInfo.isOnSale,
      release?.ppb_price_point,
    ]);

  const buildReleasePaymentSetupPayload = (
    confirmationTokenId: string,
  ): ReleasePaymentSetupPayload => {
    if (!release) {
      throw new Error("Release is required for payment setup.");
    }

    return {
      ...buildReleaseCheckoutPayload(),
      checkoutFlow: "release_payment_setup",
      releaseId: release.id,
      confirmationTokenId,
    };
  };

  type ReleasePurchaseResponse = Awaited<
    ReturnType<
      | typeof createReleasePurchaseFromConfirmationToken
      | typeof createReleasePurchaseFromSavedPaymentMethod
    >
  >;

  const updateCompletedReleaseCache = async (
    response: ReleasePurchaseResponse,
  ) => {
    const completedRelease = response.data.payload.results;
    if (!completedRelease) return;

    const releaseUuid = getRuntimeReleaseUuid(release);
    const completedReleaseUuid = getRuntimeReleaseUuid(completedRelease);

    // Direct-detail keys whose cached body may not carry `entity_type` and so
    // wouldn't be reached by the deep matcher below.
    const directDetailKeys = new Map<string, readonly unknown[]>();
    const enqueueDirectKey = (queryKey: readonly unknown[]) => {
      directDetailKeys.set(JSON.stringify(queryKey), queryKey);
    };
    if (releaseUuid) enqueueDirectKey(["releaseDetailsRelease", releaseUuid]);
    if (completedReleaseUuid) {
      enqueueDirectKey(["releaseDetailsRelease", completedReleaseUuid]);
    }
    queryClient
      .getQueriesData<ReleasePurchaseResponse>({
        queryKey: ["releaseDetailsRelease"],
      })
      .forEach(([queryKey, cachedResponse]) => {
        const cachedRelease = cachedResponse?.data?.payload?.results;
        if (
          cachedRelease?.id === completedRelease.id ||
          (completedReleaseUuid !== undefined &&
            getRuntimeReleaseUuid(cachedRelease) === completedReleaseUuid)
        ) {
          enqueueDirectKey(queryKey);
        }
      });

    directDetailKeys.forEach((queryKey) => {
      queryClient.setQueryData(queryKey, response);
    });

    patchAllQueriesWithCompletedEntity(
      queryClient,
      completedRelease,
      isMatchingReleaseEntity,
    );

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: GET_RELEASE_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: GET_COMIC_RELEASES_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: ["fetchedComicReleases"] }),
      ...Array.from(directDetailKeys.values()).map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    ]);
  };

  const finishSuccess = (completedRelease: Release) => {
    if (onSuccessProp) {
      onSuccessProp(buildHostedCheckoutReleaseSuccessResult(completedRelease));
      return;
    }
    onClose?.();
  };

  const {
    run: runReleasePurchase,
    runAndSwallow: runReleasePurchaseAndSwallow,
    isLoading: isCompletingPurchase,
    error: checkoutError,
  } = usePaymentCompletion<ReleasePurchaseResponse>({
    updateCache: updateCompletedReleaseCache,
    onSuccess: (response) => {
      const completedRelease = response.data.payload.results ?? (release as Release);
      clearGuestPendingPurchase(releasePayloadStore);
      clearPendingGoldReleaseAutoPurchase();
      finishSuccess(completedRelease);
    },
    fallbackMessage: t("components.payments.purchaseRelease.purchaseDecline"),
  });

  usePendingAuthPurchaseResume<ReleasePaymentSetupPayload>({
    enabled: !!userData,
    entityKey: release?.id,
    payloadStore: releasePayloadStore,
    payloadMatchesEntity: (payload) => payload.releaseId === release?.id,
    onResume: (payload) => {
      if (!release) return Promise.reject(new Error("Release is required."));
      return runReleasePurchase(() =>
        createReleasePurchaseFromConfirmationToken(release.id, payload),
      );
    },
  });

  const handleSavedPaymentMethodPurchase = useCallback(() => {
    if (!release) return;
    runReleasePurchaseAndSwallow(() =>
      createReleasePurchaseFromSavedPaymentMethod(
        release.id,
        buildReleaseCheckoutPayload(),
      ),
    );
  }, [release, runReleasePurchaseAndSwallow, buildReleaseCheckoutPayload]);

  const autoPurchaseAttemptedReleaseIdRef = useRef<number>();
  const shouldAutoPurchaseWithSavedPaymentMethod =
    !!autoPurchaseReleaseWithSavedPaymentMethod ||
    hasPendingGoldReleaseAutoPurchase(release?.id);

  useEffect(() => {
    if (
      !shouldAutoPurchaseWithSavedPaymentMethod ||
      !userData ||
      !release ||
      release.release_user_purchase ||
      isCompletingPurchase ||
      autoPurchaseAttemptedReleaseIdRef.current === release.id
    ) {
      return;
    }

    autoPurchaseAttemptedReleaseIdRef.current = release.id;
    clearPendingGoldReleaseAutoPurchase();
    handleSavedPaymentMethodPurchase();
  }, [
    handleSavedPaymentMethodPurchase,
    isCompletingPurchase,
    release,
    shouldAutoPurchaseWithSavedPaymentMethod,
    userData,
  ]);

  const handlePaymentSetupComplete = (confirmationTokenId: string) => {
    const payload = buildReleasePaymentSetupPayload(confirmationTokenId);

    if (!userData) {
      saveGuestPendingPurchase(payload, releasePayloadStore);
      setFunnelType(FUNNEL_TYPES.signup);
      return;
    }

    if (!release) return;
    runReleasePurchaseAndSwallow(() =>
      createReleasePurchaseFromConfirmationToken(release.id, payload),
    );
  };

  // Claimable Gold-monthly releases route through the hub claim flow.
  if (type === ReleasePricingType.ClaimableGoldMonthly) {
    return <ClaimWithGoldButton onBeforeNavigate={onClose} width="100%" />;
  }

  const submitText = isCompletingPurchase
    ? "Completing purchase..."
    : `${t("components.payments.pdf.purchaseReleaseFor")} $${price}`;

  return (
    <Flex flexDirection="column" width="100%" gap="24px">
      {release?.release_user_purchase ? (
        <PurchaseButton
          text={
            release?.user_read_status ? "Continue reading" : "Start reading"
          }
          onClick={() => {
            if (onSuccessProp) {
              onSuccessProp(buildHostedCheckoutReleaseSuccessResult(release));
            } else {
              onClose?.();
            }
          }}
        />
      ) : (
        <Flex flexDirection="column" gap="8px" width="100%">
          {isCompletingPurchase ? (
            // Hide the embedded form during the post-card-submit API call so
            // the user doesn't see a second card form while the purchase is
            // being finalized — that stale form invited a second submit click
            // and made the post-auth resume look like nothing happened.
            <PurchaseButton
              text="Confirming your purchase…"
              loading
              isDisabled
            />
          ) : showEmbeddedForm ? (
            <EmbeddedStripePaymentForm
              submitButtonText={submitText}
              disabled={isCompletingPurchase}
              onComplete={handlePaymentSetupComplete}
            />
          ) : (
            <PurchaseButton
              text={submitText}
              onClick={handleSavedPaymentMethodPurchase}
              loading={isCompletingPurchase}
              isDisabled={isCompletingPurchase}
            />
          )}
          {checkoutError ? (
            <Text color="error.300" fontSize="14px" lineHeight="130%">
              {checkoutError}
            </Text>
          ) : null}
        </Flex>
      )}
    </Flex>
  );
}
